import Effects from 'effects/Effects.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AudioParamValueParameter,
        AbstractAudioParamParameter,
        } from 'parameters/AbstractAudioParamParameter.js';

class UltraPhaser extends Effects.Effect {
    constructor(sequencerEngine) {
        super("Ultra Phaser", () => new Tone.Gain(1), sequencerEngine);

        this.poles = 8;
        this.generateAllPassFilters();

        this.rateParameter = new AudioParamValueParameter(
            'Rate', this.lfo.frequency, {
                documentation: "The speed of the sweeping.",
                defaultValue: 0.25,
                min: 0,
                max: 16}
            );

        this.registerParameter(this.rateParameter);

        this.feedbackParameter = new AudioParamValueParameter(
            'Feedback', this.feedbackNode.gain, {
                documentation: "The amount of signal that is fed back into the effect.",
                defaultValue: 0.1,
                min: 0,
                max: 1}
            );

        this.registerParameter(this.feedbackParameter);

        this.wetParameter = new AudioParamValueParameter(
            'Wet', this.crossFade.fade, {
                documentation: "Controls the amount of (wet) effected signal, vs. dry (un-effected) signal.",
                defaultValue: 0.5,
                min: 0,
                max: 1}
            );

        this.registerParameter(this.wetParameter);

        this.depthParameter = new AbstractAudioParamParameter(
            'Depth',
            this.lfo._scaler._scale, (o, x) => {
                this.lfo.max = x;
            },
            {
                documentation: "The depth of the modulation, controlling how much it sweeps on each cycle.",
                min:0,
                max: 1,
                unit: '',
                defaultValue: 0.5,
                automatable: false});
        
        this.registerParameter(this.depthParameter);
        this.updateInitialValues();
    }

    dispose() {
        super.dispose();

        this.filters.forEach(x => x.dispose());
        this.scalers.forEach(x => x.dispose());
        this.crossFade.dispose();
        this.lfo.dispose();
        this.feedbackNode.dispose();
        this.outputNode.dispose();

        this.filters = undefined;
        this.scalers = undefined;
        this.crossFade = undefined;
        this.lfo = undefined;
        this.feedbackNode = undefined;
        this.outputNode = undefined;

        this.feedbackParameter = undefined;
        this.depthParameter = undefined;
        this.wetParameter = undefined;
        this.rateParameter = undefined;
    }

    generateAllPassFilters() {
        this.filters = [
        ];

        this.scalers = [
        ];

        let frequency = 100;
        for (let i=0; i < this.poles; i++) {
            let filter = new Tone.Filter(frequency, 'allpass');
            this.filters.push(filter);
            let scaler = new Tone.Scale(frequency, frequency+4000);
            scaler.connect(filter.frequency);
            this.scalers.push(scaler);
            frequency += 1000;
        }


        this.outputNode = new Tone.Gain(1);
        this.lfo = new Tone.LFO('1n', 0, 1);
        this.filters.forEach((f,i) => this.lfo.connect(this.scalers[i]));

        let prev = this.effectNode; 
        for (let i in this.filters) {
            prev.connect(this.filters[i]);
            prev = this.filters[i];
        }
        this.lfo.start();
        this.crossFade = new Tone.CrossFade(0);
        this.crossFade.connect(this.outputNode);
        this.effectNode.connect(this.crossFade, 0, 0);
        prev.connect(this.crossFade, 0, 1);
        this.feedbackNode = new Tone.Gain(0.2);
        prev.connect(this.feedbackNode);
        this.feedbackNode.connect(this.filters[0]);
    }

    documentation() {
        return "Sweeping effect where signal is filtered, and the position of peaks and troughs are modulated and varying.";
    }

    connect(x) {
        this.outputNode.connect(x);
    }

    disconnect(node) {
        this.outputNode.disconnect(node);
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new UltraPhaser(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    getJson() {
        let json = {
            ...super.getJson(),
            rate: this.rateParameter.get(),
            feedback: this.feedbackParameter.get(),
            wet: this.wetParameter.get(),
            depth: this.depthParameter.get()
        };

        json = this.saveParameterToJson(json, 'rateParameter', this.rateParameter);
        json = this.saveParameterToJson(json, 'feedbackParameter', this.feedbackParameter);
        json = this.saveParameterToJson(json, 'wetParameter', this.wetParameter);
        json = this.saveParameterToJson(json, 'depthParameter', this.depthParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        if (!isNaN(json.rate) && json.rate) {
            bulkUpdate(this.rateParameter, json.rate, bulk);
        }
        bulkUpdate(this.feedbackParameter, json.feedback, bulk);
        bulkUpdate(this.wetParameter, json.wet, bulk);
        bulkUpdate(this.depthParameter, json.depth, bulk);

        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'rateParameter', this.rateParameter, isCopy),
            ... this.loadParameterFromJson(json, 'feedbackParameter', this.feedbackParameter, isCopy),
            ... this.loadParameterFromJson(json, 'wetParameter', this.wetParameter, isCopy),
            ... this.loadParameterFromJson(json, 'depthParameter', this.depthParameter, isCopy)
        };
    }
}

export default UltraPhaser;
